<template>
  <div class="w-full">
    <!-- Tab Navigation -->
    <div :class="tabsNavClasses">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.id || index"
        type="button"
        :class="[
          tabClasses,
          isActive(tab, index) ? activeTabClasses : inactiveTabClasses,
          tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        ]"
        :disabled="tab.disabled"
        @click="selectTab(tab, index)"
      >
        <!-- Icon -->
        <span v-if="tab.icon" class="mr-2">
          <component :is="tab.icon" class="w-4 h-4" />
        </span>

        <!-- Label -->
        <span>{{ tab.label }}</span>

        <!-- Badge -->
        <span
          v-if="tab.badge !== undefined"
          :class="[
            'ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded-full',
            isActive(tab, index)
              ? 'bg-primary/10 text-primary'
              : 'bg-gray-100 text-gray-500'
          ]"
        >
          {{ tab.badge }}
        </span>
      </button>
    </div>

    <!-- Tab Content -->
    <div :class="contentClasses">
      <slot :active-tab="activeTab" :active-index="activeIndex">
        <template v-for="(tab, index) in tabs" :key="tab.id || index">
          <div v-show="isActive(tab, index)">
            <slot :name="tab.id || `tab-${index}`" :tab="tab"></slot>
          </div>
        </template>
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseTabs',
  props: {
    tabs: {
      type: Array,
      required: true,
      validator: (value) => {
        return value.every(tab => tab.label)
      }
    },
    modelValue: {
      type: [String, Number],
      default: 0
    },
    variant: {
      type: String,
      default: 'underline',
      validator: (value) => ['underline', 'pills', 'boxed'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    contentPadding: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg'].includes(value)
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      internalValue: this.modelValue
    }
  },
  computed: {
    activeTab() {
      if (typeof this.internalValue === 'string') {
        return this.tabs.find(tab => tab.id === this.internalValue)
      }
      return this.tabs[this.internalValue]
    },
    activeIndex() {
      if (typeof this.internalValue === 'string') {
        return this.tabs.findIndex(tab => tab.id === this.internalValue)
      }
      return this.internalValue
    },
    tabsNavClasses() {
      const baseClasses = 'flex'
      const variantClasses = {
        underline: 'border-b border-gray-200 gap-0',
        pills: 'gap-1 p-1 bg-gray-100 rounded-lg',
        boxed: 'border border-gray-200 rounded-lg overflow-hidden'
      }
      return [
        baseClasses,
        variantClasses[this.variant],
        this.fullWidth ? 'w-full' : ''
      ]
    },
    tabClasses() {
      const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-2.5 text-base'
      }
      const baseClasses = 'inline-flex items-center font-medium transition-all duration-200 focus:outline-none border-0 bg-transparent'
      const widthClass = this.fullWidth ? 'flex-1 justify-center' : ''
      return [baseClasses, sizes[this.size], widthClass]
    },
    activeTabClasses() {
      const variants = {
        underline: 'text-primary border-b-2 border-primary -mb-px',
        pills: 'bg-white text-gray-900 rounded-md',
        boxed: 'bg-primary text-white'
      }
      return variants[this.variant]
    },
    inactiveTabClasses() {
      const variants = {
        underline: 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent -mb-px',
        pills: 'text-gray-500 hover:text-gray-700',
        boxed: 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
      }
      return variants[this.variant]
    },
    contentClasses() {
      const paddings = {
        none: 'pt-0',
        sm: 'pt-3',
        md: 'pt-4',
        lg: 'pt-6'
      }
      return paddings[this.contentPadding]
    }
  },
  watch: {
    modelValue(newValue) {
      this.internalValue = newValue
    }
  },
  methods: {
    isActive(tab, index) {
      if (typeof this.internalValue === 'string') {
        return tab.id === this.internalValue
      }
      return index === this.internalValue
    },
    selectTab(tab, index) {
      if (tab.disabled) return

      const value = tab.id || index
      this.internalValue = value
      this.$emit('update:modelValue', value)
      this.$emit('change', { tab, index })
    }
  }
}
</script>
